import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { SendBundleValidationResult, SendSeverityLevel } from '@/model/Agent.model';
import { useMemo, type FC } from 'react';
import { sendDatasetLabel, sendDatasetOf } from '@/components/Agents/SendValidator/utils/SendDomains';
import SendOmissionNotice from './SendOmissionNotice';
import SendSeverityBadge from './SendSeverityBadge';
import { severityLevelOf } from '@/components/Agents/SendValidator/utils/SendSeverity';
import { partitionFindings } from '@/components/Agents/SendValidator/utils/SendFindingPartition';
import {
  AI_REVIEW_FAILED_TEXT,
  aiReviewCoverageTextOf,
  aiReviewOutcomeOf,
  IN_VIVO_AI,
  IN_VIVO_AI_SHORT,
} from '@/components/Agents/SendValidator/utils/SendAiUse';
import {
  aiNoteDomainsLabel,
  domainSetLabel,
  fileKeysOf,
  findingLocationOf,
  formatCount,
} from '@/components/Agents/SendValidator/utils/SendText';
import styles from './SendResults.module.scss';

/** One Benchling In Vivo AI note, with what it was about. */
interface SendAiNote {
  key: string;
  /** The file or domain set the note was made on. */
  about: string;
  /** Where in the file, when the note names a column or row. */
  where?: string;
  message: string;
  /** Always advisory: the server's level for a Benchling In Vivo AI note, or what an older one reads as. */
  level: SendSeverityLevel;
}

interface SendAiNotesProps {
  result: SendBundleValidationResult;
  testId?: string;
}

/** Every AI note the result carries, file notes first in file order, then the cross-domain ones. */
const aiNotesOf = (result: SendBundleValidationResult): Array<SendAiNote> => {
  const notes: Array<SendAiNote> = [];
  const fileKeys = fileKeysOf(result.per_domain);
  result.per_domain.forEach((file, fileIndex) => {
    partitionFindings(file.findings).aiNotes.forEach((note, index) => {
      notes.push({
        key: `${fileKeys[fileIndex]}-${index}`,
        about: sendDatasetLabel(sendDatasetOf(file)),
        where: findingLocationOf(note),
        message: note.message,
        level: severityLevelOf(note),
      });
    });
  });
  result.cross_domain.forEach((group) => {
    partitionFindings(group.findings).aiNotes.forEach((note, index) => {
      notes.push({
        key: `${domainSetLabel(group.domains)}-${index}`,
        about: aiNoteDomainsLabel(group.domains, result.per_domain),
        where: note.key_variable ?? undefined,
        message: note.message,
        level: severityLevelOf(note),
      });
    });
  });
  return notes;
};

/**
 * Benchling In Vivo AI's notes on the run, in a section of their own and never inside a check's.
 * They explain; they decide nothing. Each is Advisory, the level only a model's note carries, and
 * none is counted in a check's numbers or turns a result into a pass or a failure. The model behind them is never named. The count
 * is the server's, and any note a limit left out is announced. Renders nothing when there are none,
 * unless the review did not complete or did not reach every file with findings, which is always
 * said here.
 */
const SendAiNotes: FC<SendAiNotesProps> = ({ result, testId = 'invivo-send-validator__ai-notes' }) => {
  const notes = useMemo(() => aiNotesOf(result), [result]);
  const { observations, omitted_observations: notShown } = result.report.advisory_counts;
  const reviewFailed = aiReviewOutcomeOf(result.run_metadata) === 'failed';
  const coverage = aiReviewCoverageTextOf(result.run_metadata);
  // Read from the server's count, never from the notes carried, which a limit may have cut to none. A
  // review that did not complete, or did not reach every file, is said here even when it left no
  // notes, so it never reads as one with nothing to add.
  if (observations === 0 && !reviewFailed && _isNil(coverage)) {
    return null;
  }
  return (
    <div className={`ui-card pa4 ${styles.aiNotes}`} data-test-component="SendAiNotes" data-testid={testId}>
      <div className="flex items-center">
        <h2 className={`${styles.sectionTitle} mr2`}>{IN_VIVO_AI_SHORT} notes</h2>
        <span
          className={`f7 lh-title dib br2 ba ph2 pv1 near-black b--moon-gray bg-moon-gray ${styles.nowrap}`}
          data-test-element="ai-notes-count"
        >
          {formatCount(observations)}
        </span>
      </div>
      <p className="f6 mid-gray ma0 mt1 mb2" data-test-element="ai-notes-subtitle">
        From {IN_VIVO_AI} · advisory, confirm before acting
      </p>
      <p className="f6 dark-gray ma0 mb3" data-test-element="ai-notes-explainer">
        These notes explain the results. They are model-generated, so confirm each one before acting on it, and they may
        not appear the same way if the bundle is checked again. They are not part of the validation checks, decide
        nothing, and are counted in none of the checks&apos; numbers above.
      </p>
      {reviewFailed && (
        <p className={styles.aiReviewFailed} role="status" data-test-element="ai-review" data-test-key="failed">
          {AI_REVIEW_FAILED_TEXT}
        </p>
      )}
      {_notNil(coverage) && (
        <p className="f6 dark-gray ma0 mb3" role="status" data-test-element="ai-review-coverage">
          {coverage}
        </p>
      )}
      {_isNotEmpty(notes) && (
        <ul className={styles.aiNoteItems}>
          {notes.map((note) => (
            <li key={note.key} className={styles.aiNoteItem} data-test-element="ai-note">
              <div className="flex items-center" data-test-element="ai-note-heading">
                <SendSeverityBadge level={note.level} className="mr2" />
                <span className="f6 fw6 near-black" data-test-element="ai-note-about">
                  {note.about}
                  {_notNil(note.where) ? ` · ${note.where}` : ''}
                </span>
              </div>
              <p className="f6 near-black ma0 mt1">{note.message}</p>
            </li>
          ))}
        </ul>
      )}
      <SendOmissionNotice aiNotesNotShown={notShown} testId="invivo-send-validator__ai-notes-omission" />
    </div>
  );
};

export default SendAiNotes;
